import React, { useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import { SystemSettings } from '@INTERFACES/api/system-settings';
import { useUpdateSystemSettingsMutation } from '@SERVICES';

import BasicInput from '@COMPONENTS/COMMON/inputs/input/BasicInput';
import BasicTextarea from '@COMPONENTS/COMMON/inputs/textarea/BasicTextarea';
import BasicCheckbox from '@COMPONENTS/COMMON/inputs/checkbox/BasicCheckbox';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import { notify } from '@NOTIFICATION/Notificator';

import styles from './SystemSettingsTab.module.scss';

const cx: CX = classnames.bind(styles);

interface SystemSettingsTabProps {
    systemSettingsData: SystemSettings;
}

function SystemSettingsTab(props: SystemSettingsTabProps) {
    const { systemSettingsData } = props;

    const [updateSystemSettings, { isLoading }] = useUpdateSystemSettingsMutation();

    const [formData, setFormData] = useState({
        wysiwygApiToken: systemSettingsData.wysiwygApiToken || '',
        wysiwygApiUrl: systemSettingsData.wysiwygApiUrl || '',
        systemName: systemSettingsData.systemName || '',
        systemVersion: systemSettingsData.systemVersion || '',
        maintenanceMode: systemSettingsData.maintenanceMode || false,
        maintenanceMessage: systemSettingsData.maintenanceMessage || '',
    });

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            await updateSystemSettings({
                wysiwyg_api_token: formData.wysiwygApiToken,
                wysiwyg_api_url: formData.wysiwygApiUrl,
                system_name: formData.systemName,
                system_version: formData.systemVersion,
                maintenance_mode: formData.maintenanceMode,
                maintenance_message: formData.maintenanceMessage,
            }).unwrap();

            notify.success('System settings updated successfully', {
                toastId: 'system-settings-updated',
            });
        } catch (error) {
            notify.error('Failed to update system settings', {
                toastId: 'system-settings-error',
            });
        }
    };

    return (
        <div className={cx('system-settings-tab')}>
            <div className={cx('form-section')}>
                <form onSubmit={handleSubmit}>
                    <div className={cx('section')}>
                        <h3>WYSIWYG Editor Configuration</h3>
                        <div className={cx('form-group')}>
                            <BasicInput
                                label="API Token"
                                value={formData.wysiwygApiToken}
                                onChange={(value) => handleInputChange('wysiwygApiToken', value)}
                                placeholder="Enter WYSIWYG editor API token"
                            />
                        </div>
                        <div className={cx('form-group')}>
                            <BasicInput
                                label="API URL"
                                value={formData.wysiwygApiUrl}
                                onChange={(value) => handleInputChange('wysiwygApiUrl', value)}
                                placeholder="Enter WYSIWYG editor API URL"
                            />
                        </div>
                    </div>

                    <div className={cx('section')}>
                        <h3>System Information</h3>
                        <div className={cx('form-group')}>
                            <BasicInput
                                label="System Name"
                                value={formData.systemName}
                                onChange={(value) => handleInputChange('systemName', value)}
                                placeholder="Enter system name"
                            />
                        </div>
                        <div className={cx('form-group')}>
                            <BasicInput
                                label="System Version"
                                value={formData.systemVersion}
                                onChange={(value) => handleInputChange('systemVersion', value)}
                                placeholder="Enter system version"
                            />
                        </div>
                    </div>

                    <div className={cx('section')}>
                        <h3>Maintenance Mode</h3>
                        <div className={cx('form-group')}>
                            <BasicCheckbox
                                title="Enable Maintenance Mode"
                                checked={formData.maintenanceMode}
                                onChange={() => handleInputChange('maintenanceMode', !formData.maintenanceMode)}
                            />
                        </div>
                        {formData.maintenanceMode && (
                            <div className={cx('form-group')}>
                                <BasicTextarea
                                    title="Maintenance Message"
                                    value={formData.maintenanceMessage}
                                    onChange={(value) => handleInputChange('maintenanceMessage', value)}
                                    placeholder="Enter maintenance message to display to users"
                                />
                            </div>
                        )}
                    </div>

                    <div className={cx('form-actions')}>
                        <BasicButton
                            title={isLoading ? 'Saving...' : 'Save System Settings'}
                            onClick={handleSubmit}
                            isProcessing={isLoading}
                        />
                    </div>
                </form>
            </div>
        </div>
    );
}

SystemSettingsTab.propTypes = {
    systemSettingsData: PropTypes.shape({
        id: PropTypes.number.isRequired,
        wysiwygApiToken: PropTypes.string.isRequired,
        wysiwygApiUrl: PropTypes.string.isRequired,
        systemName: PropTypes.string.isRequired,
        systemVersion: PropTypes.string.isRequired,
        maintenanceMode: PropTypes.bool.isRequired,
        maintenanceMessage: PropTypes.string.isRequired,
        createdAt: PropTypes.string.isRequired,
        updatedAt: PropTypes.string.isRequired,
    }).isRequired,
};

export default SystemSettingsTab; 